<template>
  <ContentCard>
    <template #header>
      <ContentCardSubheader>
        <div class="flex items-baseline">
          {{
            isDetailView
              ? t('Reviews.product.view.campaignDetailView.reviewsTableTitle')
              : t('Reviews.product.view.reviewsTitle', { productName: product.title })
          }}
          <span v-if="isDetailView" class="ml-2 flex items-center gap-1 text-body-m text-subtlest">{{
            t('Reviews.product.view.campaignDetailView.reviewsTableSubtitle', { count: pagination.total })
          }}</span>
          <TooltipWrapper v-else class="ml-2">
            <template #trigger>
              <a :href="tooltipLink" target="_blank"><CircleHelpIcon class="lucide-icon-s text-icon-subtlest" /></a>
            </template>
            <template #content>
              <div v-html="tooltipText"></div>
            </template>
          </TooltipWrapper>
        </div>
        <template #actions>
          <div class="flex items-center gap-2">
            <template v-if="queryParams.view === SurveyResponsesViewEnum.Outdated">
              <TooltipWrapper v-if="product.configFlags.reviewRemindersAllowed">
                <template #trigger>
                  <button
                    class="btn-outline-color-s"
                    :disabled="outdatedRemindeableCount === 0"
                    data-testid="reviews-bulk-reminder-button"
                    @click="openBulkReminderDialog"
                  >
                    {{ t('Reviews.product.action.sendBulkReminder.label', { count: outdatedRemindeableCount }) }}
                    <BellIcon class="lucide-icon-s" />
                  </button>
                </template>
                <template #content>
                  <div v-html="t('Reviews.product.action.sendBulkReminder.tooltip.outdatedView')" />
                </template>
              </TooltipWrapper>
              <TooltipWrapper v-else>
                <template #trigger>
                  <button class="btn-outline-color-s" data-testid="reviews-bulk-reminder-button" disabled>
                    <LockIcon class="lucide-icon-s" />
                    {{ t('Reviews.product.action.sendBulkReminder.label', { count: outdatedRemindeableCount }) }}
                  </button>
                </template>
                <template #content>
                  <div
                    @click="onFencedUpgradeClick($event, 'bulk_reminder')"
                    v-html="t('Reviews.product.action.sendReminder.unlockFeatureTooltip')"
                  />
                </template>
              </TooltipWrapper>
            </template>
            <button
              v-if="product.hasPaidPlan"
              class="btn-outline-color-s"
              :disabled="!hasReviews"
              data-testid="export-reviews-button"
              @click="downloadCsv"
            >
              {{ t('Reviews.product.action.downloadReviewsCsv', { count: pagination.total }) }}
              <DownloadIcon class="lucide-icon-s" />
            </button>
            <TooltipWrapper v-else>
              <template #trigger>
                <button class="btn-outline-color-s" data-testid="export-reviews-button" disabled>
                  <LockIcon class="lucide-icon-s" />{{ t('Reviews.product.action.downloadReviewsCsv', { count: pagination.total }) }}
                </button>
              </template>
              <template #content>
                <div
                  @click="onFencedUpgradeClick($event, 'export_reviews')"
                  v-html="t('Reviews.product.action.disabled.downloadReviewsCsv')"
                ></div>
              </template>
            </TooltipWrapper>
          </div>
        </template>
      </ContentCardSubheader>
    </template>
    <TabBar
      v-model="queryParams.view"
      class="w-fit"
      :tabs="[
        { id: SurveyResponsesViewEnum.All, name: t('Reviews.product.view.reviewsTab.all', { count: 7 }) },
        { id: SurveyResponsesViewEnum.Outdated, name: t('Reviews.product.view.reviewsTab.outdated', { count: outdatedCount }) },
      ]"
      @update:model-value="changeViewState"
    />
    <div v-if="queryParams.view == SurveyResponsesViewEnum.Outdated" class="max-w-[600px] pt-4">
      {{ t('Reviews.product.view.reviewsTable.description') }}
    </div>
    <div class="overflow-x-auto py-6">
      <ReviewsProductReviewsFilters :purposes="purposes" :filters-disabled="disableFilters" />
      <ReviewsTable
        :view-state="queryParams.view"
        :filters-disabled="allFiltersDisabled"
        :loading="loading"
        :product-id="product.id"
        :has-paid-plan="product.hasPaidPlan || false"
        :survey-responses="result?.manage?.surveyResponses?.results || []"
      />
      <PaginationControl
        v-if="hasReviews"
        :page="pagination.page"
        :per-page="pagination.perPage"
        :total="pagination.total"
        show-range
        show-total
        show-pagination="always"
        @update:page="updatePage"
        @update:per-page="updatePerPage"
      />
      <SendReminderDialog ref="sendBulkReminderModal" :product="product" is-bulk-reminder :filters="variables.filters" />
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { TabBar, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon, DownloadIcon, LockIcon, BellIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import SendReminderDialog from '@/components/ReviewsManagement/Reviews/SendReminderDialog.vue'
import { useRouterQueryPagination } from '@/composables/useRouterQueryPagination'
import {
  type ProductData,
  SurveyResponsesSortByEnum,
  SortDirectionEnum,
  useReviewsProductSurveyResponsesQuery,
  useReviewsProductSurveyResponsesExportCsvLazyQuery,
  SurveyResponsesViewEnum,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { trackPlanUpgrade } from '@/services/planCtaTracking'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import { useSortQuery } from '@/ui/TableLight'

import ReviewsProductReviewsFilters, { type FiltersDisabledState } from './ReviewsProductReviewsFilters.vue'
import ReviewsTable from './ReviewsTable.vue'
import { useRouterQueryFilters } from './useRouterQueryReviewsFilters'
import PaginationControl from '../../PaginationControl.vue'

const props = defineProps<{
  product: ProductData
  campaignId?: string
  isDetailView?: boolean
}>()

const { t } = useI18n()
const { page, perPage, updatePage, updatePerPage } = useRouterQueryPagination()
const { sortBy, sortDirection } = useSortQuery(SurveyResponsesSortByEnum, SortDirectionEnum)
const { queryParams, updated_since, updated_to, reminder_status, reply_status, setReviewFilters, resetAllFilters } = useRouterQueryFilters()

const tooltipText = t('Reviews.product.view.campaigns.reviewsHelp')
const tooltipLink = t('Reviews.product.view.campaigns.reviewsHelpLink')

const sendBulkReminderModal = ref<InstanceType<typeof SendReminderDialog> | null>(null)

function openBulkReminderDialog() {
  sendBulkReminderModal.value?.open()
}

function onFencedUpgradeClick(event: MouseEvent, placement: string) {
  if ((event.target as HTMLElement).closest('a')) {
    trackPlanUpgrade({ entity_type: 'tool', entity_id: props.product.id, placement })
  }
}

const variables = computed(() => ({
  slug: props.product.slug,
  page: page.value,
  perPage: perPage.value,
  sortBy: sortBy.value,
  sortDirection: sortDirection.value,
  filters: {
    campaignId: props.campaignId ? [props.campaignId] : undefined,
    updatedSince: updated_since.value,
    updatedTo: updated_to.value,
    purposes: queryParams.value.purposes,
    rating: queryParams.value.ratings,
    state: queryParams.value.states,
    reminderStatus: reminder_status.value,
    replyStatus: reply_status.value,
  },
  view: queryParams.value.view,
}))

const csvExportVariables = computed(() => ({
  slug: variables.value.slug,
  filters: variables.value.filters,
  view: queryParams.value.view,
}))

const { loading, result } = useReviewsProductSurveyResponsesQuery(variables, { clientId: 'reviews' })

const purposes = computed(() => {
  return result.value?.manage?.surveyResponses?.purposesFacet
})

const hasReviews = computed(
  () => result?.value?.manage?.surveyResponses?.results && result?.value?.manage?.surveyResponses?.results.length > 0,
)

const outdatedCount = computed(
  () => result.value?.manage?.surveyResponses?.viewFacet?.find((f) => f.key === SurveyResponsesViewEnum.Outdated)?.docCount ?? 0,
)
const outdatedRemindeableCount = computed(
  () => result.value?.manage?.surveyResponses?.viewFacet?.find((f) => f.key === SurveyResponsesViewEnum.OutdatedRemindable)?.docCount ?? 0,
)

const allFiltersDisabled = computed(
  () =>
    !hasReviews.value &&
    variables.value.filters.purposes?.length === 0 &&
    variables.value.filters.rating?.length === 0 &&
    variables.value.filters.state?.length === 0 &&
    variables.value.filters.reminderStatus === null &&
    variables.value.filters.replyStatus === null &&
    variables.value.filters.updatedSince === null &&
    variables.value.filters.updatedTo === null,
)

const disableFilters = computed<FiltersDisabledState>(() => ({
  timeframe: allFiltersDisabled.value || queryParams.value.view === SurveyResponsesViewEnum.Outdated,
  purposes: allFiltersDisabled.value,
  ratings: allFiltersDisabled.value,
  states: allFiltersDisabled.value,
  interaction: allFiltersDisabled.value,
}))

const pagination = computed(() => ({
  page: result.value?.manage?.surveyResponses?.pagination.currentPage ?? page.value,
  perPage: result.value?.manage?.surveyResponses?.pagination.perPage ?? perPage.value,
  total: result.value?.manage?.surveyResponses?.pagination.totalCount ?? result.value?.manage?.surveyResponses?.results.length ?? 0,
}))

const { load: loadCsvUrl, result: csvUrlResult } = useReviewsProductSurveyResponsesExportCsvLazyQuery(csvExportVariables, {
  clientId: 'reviews',
})

async function downloadCsv() {
  track('export_download', { surface: 'dashboard', entity_type: 'tool', entity_id: props.product.id, export_type: 'reviews' })
  try {
    await loadCsvUrl()
    if (csvUrlResult.value?.manage?.surveyResponsesExportCsv.errors) {
      showNotification(t('Reviews.product.review.csvExportFailed'), 'error')
      return
    }

    if (csvUrlResult.value?.manage?.surveyResponsesExportCsv.fileUrl) {
      window.open(csvUrlResult.value.manage.surveyResponsesExportCsv.fileUrl, '_blank')
    }
  } catch {
    showNotification(t('Reviews.product.review.csvExportFailed'), 'error')
  }
}

function changeViewState() {
  resetAllFilters()
  setReviewFilters()
}
</script>
